import React, { useMemo, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RESIDENTS_MOBILE_COLORS } from '@/constants/residents/mobile/color';
import { useAppSelector } from '@/store';
import { UserType } from '@/types/residents/user';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { FaqSection, faqSectionsGuarantor, faqSectionsResident, commonErrorMessages } from '@/screens/residents/faq/faq';
import { CARD_STYLES } from '@/constants/residents/homeConstants';
import { strings } from '@/constants/residents';
import { AppConfig } from '@/constants/residents/config';
import { RootStackParamList } from '@/navigation/RootNavigator';

const SectionRow = ({ section, index }: { section: FaqSection; index: number }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <View style={styles.sectionRow}>
      <TouchableOpacity
        style={styles.sectionHeaderRow}
        accessibilityRole="button"
        onPress={() => setExpanded(!expanded)}
        testID={`HowItWorks_Row_${index}_${expanded ? 'collapse' : 'expand'}`}
        accessibilityLabel={`HowItWorks_Row_${index}`}
      >
        <Text style={styles.sectionTitle}>{section.title}</Text>
        {expanded ? (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_MINUS width={24} height={24} />
        ) : (
          <RESIDENTS_SVG_COMPONENTS.CIRCLE_PLUS width={24} height={24} />
        )}
      </TouchableOpacity>
      {expanded && <Text style={styles.sectionSubtext}>{section.subtext}</Text>}
    </View>
  );
};

export const HowItWorksScreen: React.FC = () => {
  const { user } = useAppSelector((s) => s.auth);
  const navigation = useNavigation<StackNavigationProp<RootStackParamList>>();

  const sections = useMemo<FaqSection[]>(() => {
    if (!user) return faqSectionsResident;
    return user.type === UserType.RESIDENT ? faqSectionsResident : faqSectionsGuarantor;
  }, [user]);

  const onSupportPress = () => {
    // Match Profile menu: open Feedback in in-app WebView, including user's email if present
    const base = AppConfig.URLS.FEEDBACK;
    const email = user?.email;
    const url = email ? `${base}?email=${encodeURIComponent(email)}` : base;
    navigation.navigate('WebView', { url, title: strings.profile.option.feedback });
  };

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <RESIDENTS_SVG_COMPONENTS.QUESTION_BUBBLE width={50} height={50} style={styles.headerIcon} />
        <View style={styles.headerTextCol}>
          <Text style={styles.headerTitle}>{strings.faqCard.cardTitle}</Text>
          <Text style={styles.headerSubtitle}>{strings.faqCard.cardSubtitle}</Text>
          <Text style={styles.headerSubtitle}>{strings.faqCard.cardSubtitle2}</Text>
          <TouchableOpacity accessibilityRole="button" onPress={onSupportPress} style={styles.supportButton} testID="HowItWorks_GetInTouch">
            <Text style={styles.supportButtonText}>{strings.faqCard.cardCta}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent} showsVerticalScrollIndicator={false}>
        {sections.map((section, idx) => (
          <SectionRow key={idx} section={section} index={idx} />
        ))}
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 16,
    ...CARD_STYLES,
    padding: 16,
  },
  headerIcon: {
    marginRight: 16,
  },
  headerTextCol: {
    flex: 1,
    paddingRight: 0,
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    marginBottom: 6,
  },
  headerSubtitle: {
    fontSize: 12,
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.9,
  },
  supportButton: {
    marginTop: 10,
    alignSelf: 'flex-start',
    backgroundColor: RESIDENTS_MOBILE_COLORS.BUTTON_PRIMARY,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  supportButtonText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 14,
  },
  scrollArea: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  sectionRow: {
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
  },
  sectionHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sectionTitle: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    fontSize: 15,
    fontWeight: '700',
    flex: 1,
    paddingRight: 12,
  },
  sectionSubtext: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.8,
    fontSize: 12,
    marginTop: 8,
  },
  errorTable: {
    marginTop: 12,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    borderRadius: 8,
    overflow: 'hidden',
  },
  errorRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  errorRowHeader: {
    backgroundColor: RESIDENTS_MOBILE_COLORS.BACKGROUND_PRIMARY,
  },
  errorRowLast: {
    borderBottomWidth: 0,
  },
  errorCell: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    opacity: 0.9,
    fontSize: 13,
  },
  errorHeaderText: {
    color: RESIDENTS_MOBILE_COLORS.TEXT_PRIMARY,
    fontWeight: '700',
    textAlign: 'center',
  },
  errorCellCode: {
    flex: 2,
    paddingRight: 12,
  },
  errorCellCodeDivider: {
    borderRightWidth: StyleSheet.hairlineWidth,
    borderRightColor: RESIDENTS_MOBILE_COLORS.BORDER_PRIMARY,
    paddingRight: 12,
    marginRight: 12,
  },
  errorCellAction: {
    flex: 3,
  },
});

export default HowItWorksScreen;

